<template>
    <div class="container-fluid">
        <p class="text-left text-danger" v-if="profileApprovalError">
          We see that you are attempting to connect to an account that has not been approved by compliance. To connect to a new account, first
          <a class="underline text-danger font-weight-bold" href="#" @click.prevent="unlockProfile">unlock your profile</a>.
        </p>

        <div v-if="loading" class="col-md-6 col-md-offset-3 loading">
            <img src="/wp-content/themes/snappy-theme/assets/images/loading2.gif">
        </div>

        <div v-else class="row social" :class="{ 'center' : onboarding}">
            <div v-if="!enableDisconnect" class="standard-mode">
                <div class="linkedin-social-widget">
                    <a @click="getLinkedinRedirectUrl" :class="{connected: accounts.linkedin.connected}"
                       class="social-network-option ln" id="linkedin-button">
                        <div class="icon fab fa-icon fa-linkedin-in"></div>
                        LinkedIn
                        <div v-if="accounts.linkedin.connected" class="status-icon fas fa-icon fa-check"></div>
                        <div v-if="!onboarding" class="connection-status">
                            <span v-if="accounts.linkedin.name">
                                <strong>{{ accounts.linkedin.name }}</strong> ( {{ accounts.linkedin.handle }} )
                                <br/>
                                <span class="url">{{ accounts.linkedin.url }}</span>
                                <br/>
                            </span>
                            <strong>{{ accounts.linkedin.connected ? '' : 'Click to Connect' }}</strong>
                        </div>
                    </a>

                    <select id="ln-page-select" v-if="accounts.linkedin && linkedinPages.length > 0"
                            @change="updateLinkedinPage()">
                        <option v-for="(page, index) in linkedinPages" :key="index" :value="page.id"
                                :selected="page.should_use">{{ page.page_name }}
                        </option>
                    </select>
                </div>

                <div v-if="shouldShowTwitter" class="twitter-social-widget">
                    <a @click="getTwitterRedirectUrl" :class="{connected: accounts.twitter.connected}"
                       class="social-network-option tw" id="twitter-button">
                        <div class="icon fab fa-icon fa-twitter"></div>
                        Twitter
                        <div v-if="accounts.twitter.connected" class="status-icon fas fa-icon fa-check"></div>
                        <div v-if="!onboarding" class="connection-status">
                            <span v-if="accounts.twitter.name">
                                <strong>{{ accounts.twitter.name }}</strong> ( {{ accounts.twitter.handle }} )
                                <br/>
                                <span class="url">{{ accounts.twitter.url }}</span>
                                <br/>
                            </span>
                            <strong>{{ accounts.twitter.connected ? '' : 'Click to Connect' }}</strong>
                        </div>
                    </a>
                </div>

                <div class="facebook-social-widget">
                    <a @click="getFacebookRedirectUrl" :class="{connected: accounts.facebook.connected}"
                       class="social-network-option fb" id="facebook-button">
                        <div class="icon fab fa-icon fa-facebook-f"></div>
                        Facebook
                        <div v-if="accounts.facebook.connected" class="status-icon fas fa-icon fa-check"></div>
                        <div v-if="!onboarding" class="connection-status">
                            <span v-if="accounts.facebook.name">
                                <strong>{{ accounts.facebook.name }}</strong> ( {{ accounts.facebook.handle }} )
                                <br/>
                                <span class="url">{{ accounts.facebook.url }}</span>
                                <br/>
                            </span>
                            <strong>{{ accounts.facebook.connected ? '' : 'Click to Connect' }}</strong>
                        </div>
                    </a>

                    <select id="fb-page-select" v-if="accounts.facebook && facebookPages.length > 0"
                            @change="updateFacebookPage()">
                        <option v-for="(page, index) in facebookPages" :key="index" :value="page.id"
                                :selected="page.should_use">{{ page.page_name }}
                        </option>
                    </select>
                </div>
            </div>

            <div v-else class="disconnect-mode">
                <div @click="disconnect('linkedin')" class="social-network-option ln"
                     :class="{disconnected: !accounts.linkedin.connected}">
                    <div class="icon fab fa-icon fa-linkedin-in"></div>
                    LinkedIn

                    <div v-if="accounts.linkedin.connected" class="status-icon fas fa-icon fa-times"></div>

                    <div class="connection-status">
                        {{ accounts.linkedin.connected ? 'Click to Disconnect' : 'Not Connected' }}
                    </div>
                </div>

                <div v-if="shouldShowTwitter" @click="disconnect('twitter')" class="social-network-option tw"
                     :class="{disconnected: !accounts.twitter.connected}">
                    <div class="icon fab fa-icon fa-twitter"></div>
                    Twitter

                    <div v-if="accounts.twitter.connected" class="status-icon fas fa-icon fa-times"></div>

                    <div class="connection-status">
                        {{ accounts.twitter.connected ? 'Click to Disconnect' : 'Not Connected' }}
                    </div>
                </div>

                <div @click="disconnect('facebook')" class="social-network-option fb"
                     :class="{disconnected: !accounts.facebook.connected}">
                    <div class="icon fab fa-icon fa-facebook-f"></div>
                    Facebook

                    <div v-if="accounts.facebook.connected" class="status-icon fas fa-icon fa-times"></div>

                    <div class="connection-status">
                        {{ accounts.facebook.connected ? 'Click to Disconnect' : 'Not Connected' }}
                    </div>
                </div>
            </div>
            <div v-if="!onboarding && accounts" @click="enableDisconnect = !enableDisconnect"
                 class="disconnect-accounts">
                {{
                    enableDisconnect ? 'Select the Social Network you wish to disconnect' : 'Click here to disconnect a Social Network'
                }}
            </div>
        </div>
    </div>
</template>

<script>

import axios from 'axios';
import swal from 'sweetalert2';

export default {
    name: 'link-social-accounts',
    data() {
        return {
            accounts: [],
            facebookPages: [],
            linkedinPages: [],
            enableDisconnect: false,
            loading: true,
            shouldShowTwitter: true,
            pageSelectionError: false,
        }
    },
    props: {
        hideTwitterHash: {
            required: true,
            type: String,
        },
        vesselId: {
            required: true,
            type: Number
        },
        snappyBrain: {
            required: true,
            type: String
        },
        redirectUrl: {
            required: true,
            type: String
        },
        onboarding: {
            required: false,
            type: Boolean,
            default: false
        },
        profileLocked: {
            type: Boolean,
            required: true,
            default: false,
        },
        profileApproved: {
            type: Boolean,
            required: true,
            default: false,
        },
    },
    computed: {
        profileApprovalError() {
            const error = new URLSearchParams(window.location.search).get('error');
            return this.profileLocked && (this.pageSelectionError || error === 'profile_approval_error');
        }
    },
    methods: {
        unlockProfile() {
          swal.fire({
            title: 'Are you sure?',
            text: 'Unlocking your profile will require that you download a new profile PDF for compliance submission, and your account will be locked until compliance approves it.',
            icon: 'warning',
            width: 550,
            padding: '3em',
            showCancelButton: true,
            confirmButtonText: 'Yes',
            buttonsStyling: false,
            customClass: {
              confirmButton: 'btn btn-lg btn-primary',
              cancelButton: 'btn btn-lg btn-primary preview',
            }
          }).then((result) => {
            if (!result.value) {
              return;
            }
            axios.post('/api/?entity=user&action=unlockProfile', {
              _method: 'PATCH'
            }).then(() => {
              window.location.href = '/account/networks';
            }).catch(() => {
              swal.fire({
                title: 'UhOh!',
                text: 'There was an error, please try again later.',
                icon: 'error',
                confirmButtonText: 'Ok',
                buttonsStyling: false,
                customClass: {
                  confirmButton: 'btn btn-lg btn-primary'
                }
              });
            });
          });
        },

        updateFacebookPage() {
            if (this.profileLocked) {
                this.pageSelectionError = true;
                return;
            }
            let el = document.getElementById('fb-page-select');
            let facebookPageSelected = el.options[el.selectedIndex].value;

            axios.post('/api/?entity=vessel&action=updateFacebookPage',
                {'pageId': facebookPageSelected}).then(() => {
                $.notify('Facebook Page Updated!', 'success');
            }).catch(() => {
                this.pageSelectionError = true;
            });
        },

        updateLinkedinPage() {
            if (this.profileLocked) {
                this.pageSelectionError = true;
                return;
            }
            let el = document.getElementById('ln-page-select');
            let linkedinPageSelected = el.options[el.selectedIndex].value;

            axios.post('/api/?entity=vessel&action=updateLinkedinPage',
                {'pageId': linkedinPageSelected}).then(() => {
                $.notify('Linkedin Page Updated!', 'success');
            }).catch(() => {
              this.pageSelectionError = true;
            });
        },

        disconnect(network) {
              let networkName = this.capitalizeFirstLetter(network);
              let status = confirm('Are you sure you want to disconnect ' + networkName + '?');

              if (status) {
                  axios.post('/api/?entity=vessel&action=disconnectSocialNetwork', {'network': network}).then(() => {
                      $.notify(networkName + ' Account Disconnected!', 'success');
                      location.reload();
                  });
              }
        },

        capitalizeFirstLetter(string) {
            return string.charAt(0).toUpperCase() + string.slice(1);
        },

        getLinkedinRedirectUrl() {
              axios.post('/api/?entity=vessel&action=getLinkedinRedirectUrl', {
                  redirect_url: this.redirectUrl
              }).then(({data: response}) => {
                  window.open(response.data.redirect_url, '_blank');
              });
        },

        getFacebookRedirectUrl() {
              axios.post('/api/?entity=vessel&action=getFacebookRedirectUrl', {
                  redirect_url: this.redirectUrl
              }).then(({data: response}) => {
                  window.open(response.data.redirect_url, '_blank');
              });
        },

        getTwitterRedirectUrl() {
            axios.post('/api/?entity=vessel&action=getTwitterRedirectUrl', {
                redirect_url: this.redirectUrl
            }).then(({data: response}) => {
                window.open(response.data.redirect_url, '_blank');
            });
        },
    },
    mounted() {
        if (['19-69-production', '26-204-production', '1-666999-staging'].includes(this.hideTwitterHash)) {
            this.shouldShowTwitter = false;
        }

        axios.get(
            '/api/?entity=vessel&action=getSocialNetworkStatus',
        ).then(({data: response}) => {
            this.accounts = response.data;
            return this.accounts;
        }).then((accounts) => {
            if (!!accounts) {
                if (accounts.facebook && accounts.facebook.connected) {
                    let self = this;

                    axios.post("/api/?entity=vessel&action=getFacebookPages").then(({data: response}) => {
                        self.facebookPages = response.data;
                    }).catch(() => console.log("Facebook connection lost"));
                }

                if (accounts.linkedin && accounts.linkedin.connected) {
                    let self = this;

                    axios.post("/api/?entity=vessel&action=getLinkedinPages").then(({data: response}) => {
                        self.linkedinPages = response.data;
                    }).catch(() => console.log("LinkedIn connection lost"));
                }
            }

            this.loading = false;
        });
    }
}
</script>

<style lang="scss">
.center {
    width: 270px;
    display: block;
    margin: 0 auto;

    .social-network-option {
        width: 100% !important;
    }
}

.row.social {
    position: relative;
}

#link-social-accounts {

    $facebook: #3B5998;
    $linkedin: #0077B5;
    $twitter: #1DA1F2;
    $disabled: #CCCCCC;
    $cancel: #993333;

    .social-network-option {
        width: 270px;
        height: 40px;
        line-height: 40px;
        font-size: 26px;
        font-weight: bold;
        color: #fff;
        position: relative;
        margin: 0 0 60px 0;
        display: block;
        cursor: pointer;
        background: $disabled;
        text-decoration: none;

        .icon {
            position: absolute;
            left: 0;
            top: 0;
            width: 40px;
            height: 40px;
            line-height: 40px;
            padding: 0 5px;
            color: #fff;
            background: $disabled;
        }

        .status-icon {
            position: absolute;
            right: 0;
            top: 0;
            width: 40px;
            height: 40px;
            line-height: 40px;
            padding: 0 5px;
            color: #fff;
        }

        .connection-status {
            position: absolute;
            left: 0;
            bottom: -62px;
            height: 60px;
            color: #777;
            font-size: 12px;
            line-height: 20px;
            font-weight: normal;
            text-align: left;

            .url {
                color: rgba(0, 0, 0, 0.3);
            }
        }
    }

    .standard-mode {

        .fb.connected {
            background: lighten($facebook, 5%);

            .icon, .status-icon {
                background: $facebook;
            }
        }

        .tw.connected {
            background: lighten($twitter, 5%);

            .icon, .status-icon {
                background: $twitter;
            }
        }

        .ln.connected {
            background: lighten($linkedin, 5%);

            .icon, .status-icon {
                background: $linkedin;
            }
        }

        .social-network-option:hover {
            &.fb {
                background: darken($facebook, 5%);

                .icon {
                    background: $facebook;
                }
            }

            &.tw {
                background: darken($twitter, 5%);

                .icon {
                    background: $twitter;
                }
            }

            &.ln {
                background: darken($linkedin, 5%);

                .icon {
                    background: $linkedin;
                }
            }
        }
    }

    .disconnect-mode {

        .fb {
            background: lighten($cancel, 5%);

            .icon, .status-icon {
                background: $cancel;
            }
        }

        .tw {
            background: lighten($cancel, 5%);

            .icon, .status-icon {
                background: $cancel;
            }
        }

        .ln {
            background: lighten($cancel, 5%);

            .icon, .status-icon {
                background: $cancel;
            }
        }

        .fb.disconnected {
            background: lighten($disabled, 5%);

            .icon, .status-icon {
                background: $disabled;
            }
        }

        .tw.disconnected {
            background: lighten($disabled, 5%);

            .icon, .status-icon {
                background: $disabled;
            }
        }

        .ln.disconnected {
            background: lighten($disabled, 5%);

            .icon, .status-icon {
                background: $disabled;
            }
        }

        .social-network-option.fb:hover {
            background: lighten($cancel, 10%);

            .icon, .status-icon {
                background: lighten($cancel, 5%);
            }
        }

        .social-network-option.tw:hover {
            background: lighten($cancel, 10%);

            .icon, .status-icon {
                background: lighten($cancel, 5%);
            }
        }

        .social-network-option.ln:hover {
            background: lighten($cancel, 10%);

            .icon, .status-icon {
                background: lighten($cancel, 5%);
            }
        }
    }

    .connected, .disconnected {
        cursor: default;
        pointer-events: none;
    }

    .facebook-social-widget {
        position: relative;

        #fb-page-select {
            border: 1px solid $facebook;
            background: $facebook;
            color: white;
            font-size: 16px;
            padding: 2px 0;
            position: absolute;
            top: 0;
            left: 280px;
            cursor: pointer;
        }
    }

    .linkedin-social-widget {
        position: relative;

        #ln-page-select {
            border: 1px solid $linkedin;
            background: $linkedin;
            color: white;
            font-size: 16px;
            padding: 2px 0;
            position: absolute;
            top: 0;
            left: 280px;
            cursor: pointer;
        }
    }
}

.loading {
    img {
        width: 75px;
    }
}
</style>
